"use strict";
/**
 * @license
 * Copyright 2016 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
export class CancellationError extends Error {
  constructor() {
    super(...arguments);
    this.name = "CancellationError";
    this.message = "CANCELED";
  }
  toString() {
    return "CANCELED";
  }
}
export const CANCELED = new CancellationError();
export function throwIfCanceled(token) {
  if (token.isCanceled === true) {
    throw CANCELED;
  }
}
const noopFunction = () => {
};
export const uncancelableToken = {
  isCanceled: false,
  add: () => noopFunction,
  remove: noopFunction
};
export class CancellationTokenSource {
  /**
   * Trigger cancellation.
   *
   * If this.isCanceled === false, then each registered cancellation handler is invoked
   * synchronously.
   */
  cancel() {
    const { handlers } = this;
    if (handlers !== null) {
      this.handlers = null;
      if (handlers !== void 0) {
        for (const handler of handlers) {
          handler();
        }
      }
    }
  }
  get isCanceled() {
    return this.handlers === null;
  }
  add(handler) {
    let { handlers } = this;
    if (handlers === null) {
      handler();
      return noopFunction;
    }
    if (handlers === void 0) {
      handlers = this.handlers = /* @__PURE__ */ new Set();
    }
    handlers.add(handler);
    return () => {
      this.remove(handler);
    };
  }
  remove(handler) {
    const { handlers } = this;
    if (handlers != null) {
      handlers.delete(handler);
    }
  }
}
export class MultipleConsumerCancellationTokenSource extends CancellationTokenSource {
  constructor() {
    super(...arguments);
    this.consumers = /* @__PURE__ */ new Set();
  }
  addConsumer(cancellationToken = uncancelableToken) {
    const { consumers } = this;
    if (consumers.has(cancellationToken) || cancellationToken.isCanceled) {
      return;
    }
    consumers.add(cancellationToken);
    cancellationToken.add(() => {
      consumers.delete(cancellationToken);
      if (consumers.size === 0) {
        this.cancel();
      }
    });
  }
}
export function makeCancelablePromise(cancellationToken, executor) {
  return new Promise((resolve, reject) => {
    if (cancellationToken === uncancelableToken) {
      executor(resolve, reject, uncancelableToken);
      return;
    }
    const scopedToken = new CancellationTokenSource();
    const unregister = cancellationToken.add(() => {
      scopedToken.cancel();
    });
    executor(
      (value) => {
        unregister();
        resolve(value);
      },
      (error) => {
        unregister();
        reject(error);
      },
      scopedToken
    );
  });
}
